<script lang="ts">
	import type { Page } from "@sveltejs/kit";

	import { Vertical, verticals } from "$lib/def/verticals";
  import { KEY_FOOD_ITEM, KEY_SEARCH, KEY_SEARCH_FOCUS, KEY_SEARCH_VERTICAL } from "$lib/serv/food-landing";
	import { query } from "$lib/util/url";
  import { page as to_be_deleted_page_import } from "$app/stores";
  import { fromStore } from "$lib/util/obs";
  import { map, of, switchMap } from "rxjs";
  import { getMenu, normalise } from "$lib/serv/restaurant";
  import { DIVIDER } from "../menu/server";

	import RestaurantPDP from '$lib/ui/modals/RestaurantPdpModal.svelte';
	import ItemSearchV2 from '$lib/ui/home/food/ItemSearchV2.svelte';
	import FoodSearch from "$lib/ui/home/food/Search.svelte";

	export let page: Page;

	$: vertical = page.url.searchParams.get('vertical') as Vertical ?? Vertical.Restaurant;
	$: searchVertical = page.url.searchParams.get(KEY_SEARCH_VERTICAL) ?? vertical;
	$: hasFoodItem = !!page.url.searchParams.get(KEY_FOOD_ITEM)?.length;


  // TODO: remove page store
  const menu$ = fromStore(to_be_deleted_page_import).pipe(
    map(p => p.url.searchParams.get(KEY_FOOD_ITEM)),
    switchMap(fooditem => {
      if (fooditem) {
        return of({ payload:undefined, error: 'Menu item not specified.'});
      }
      const [slug, , menuId] = fooditem?.split(DIVIDER) ?? [];
      return getMenu(+menuId, slug).pipe(
        map(menu => ({ payload: normalise(menu) ?? undefined }))
      );
    })
  )

  function genFocusLink(url: typeof page.url, focus: string) {
    return query(url).set(KEY_SEARCH_FOCUS, focus).toUrl();
  }
  function isFocusActive(url: typeof page.url, focus: string, includeNull = false) {
    if (includeNull && !query(url).get(KEY_SEARCH_FOCUS)) return true;
    return query(url).get(KEY_SEARCH_FOCUS) === focus;
  }

</script>

<div class="vertical-switch">
	<a href={`/?${query(page.url).toggle('search_vertical', Vertical.Restaurant)}`}
		class:active={searchVertical === Vertical.Restaurant}
		class:bold={searchVertical === Vertical.Restaurant}
		class:book={searchVertical !== Vertical.Restaurant}
		class="label-xl">
			{verticals.find((v) => v.slug === Vertical.Restaurant)?.title}
	</a>
	<a href={`/?${query(page.url).toggle('search_vertical', Vertical.Store)}`}
		class:active={searchVertical === Vertical.Store}
		class:bold={searchVertical === Vertical.Store}
		class:book={searchVertical !== Vertical.Store}
		class="label-xl">
			{verticals.find((v) => v.slug === Vertical.Store)?.title}
	</a>
</div>

<div class="search-container">
	<div class="search-switch">
		<!-- <h3 class="headline-s bold">Showing:</h3> -->
		<div class="control-bg">
			<div class="control">
        <a href={genFocusLink(page.url, 'merchant')} class:active={isFocusActive(page.url, 'merchant', true)}>
          <span class="label-xl medium">{searchVertical === Vertical.Restaurant ? 'Restaurants' : 'Shops'}</span>
        </a>
        <a href={genFocusLink(page.url, 'item')} class:active={isFocusActive(page.url, 'item')}>
          <span class="label-xl medium">{searchVertical === Vertical.Restaurant ? 'Dishes' : 'Items'}</span>
        </a>
			</div>
		</div>
	</div>
	<div class="search-results">
		{#if isFocusActive(page.url, 'item')}
      <ItemSearchV2 searchParams={{ query: page.url.search, vertical: searchVertical, term: query(page.url).get(KEY_SEARCH) ?? '' }} />
      <!--<ItemSearch searchParams={{ pathname: page.url.pathname, full_query: page.url.search, vertical: searchVertical, search: search ?? '' }} />-->
		{:else}
      <FoodSearch urlSearchParams={query(page.url).toObject()} />
		{/if}
	</div>
</div>

{#if hasFoodItem && menu$}
  {@const menuItemHash = $to_be_deleted_page_import.url.searchParams.get(KEY_FOOD_ITEM)}
  <RestaurantPDP menu={$menu$?.payload} {menuItemHash} currentUrl={page.url} hostedByRestaurantPage={false} />
{/if}

<style lang="scss">
  @use "color";
  @use "screen";
  @use "layout";
  @use "margin";

  .vertical-switch {
    @include margin.medium;
    position: sticky;
    top: calc(59px + layout.$header-height);
    background-color: color.$background-elevation-3dp;
    z-index: 2;
    border-bottom: 1px solid color.$separator;
    margin-top: -2.5rem !important; // sorry dude, the margins for for vertical-switch seem to be set from multiple files, had to override.
    margin-bottom: 3rem !important;
    a {
			display: inline-block;
      padding: 1.1rem;

      &.active {
        border-bottom: 5px solid color.$on-background-high-importance;
      }
    }
  }

  .search-container {
    position: relative;

    display: flex;
    align-items: flex-start;
  }

  .search-results {
    flex-grow: 2;
  }

  .search-switch {
		position: fixed;
    bottom: -1%;
    left: 50%;
    margin-right: 0;
    transform: translate(-50%, -50%);
    z-index: 2;

    @media (screen.$md) {
      left: calc(50% + (layout.$nav-width / 2));
      bottom: 24px;
    }

    .control-bg {
      background-color: color.$background-web;
      border-radius: 50px;
    }

    .control {
      display: flex;
      background-color: color.$object-elevation-6dp;

      border-radius: inherit;
      padding: 0.4rem;

      a {

        padding: 0.5rem 1rem;
        border-radius: inherit;


        &:nth-child(1) {
          margin-right: 1rem;
        }

        &:hover {
          cursor: pointer;
        }

        &.active {
          background-color: color.$search-switch-button-background;
          box-shadow: inset 0px 0px 0px 2px color.$separator, 0 0 7px rgba(0,0,0,0.3);
        }
      }
    }
  }
</style>